'use client';

import { Button, Drawer } from '@sprd/sprd-component-kit/v2';
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useAddItem, useAddVoucher } from '@/hooks/useCartHooks';
import { useIsMultiCouponEnabled } from '@/hooks/useIsMultiCouponEnabled';
import { useIsDesktop } from '@/hooks/useMediaQuery';
import { SizeSelectorDesktop, SizeSelectorMobile } from '@/app/[abTest]/[locale]/(spreadshirt)/cyo/assortment/detail/components/SizeSelectors';
import { pdpParamsWithSyncAtom } from '@/atoms/pdpParamsAtom';
import { PromoCode } from '@/atoms/promoAtom';
import { FavoriteButton } from '@/components/FavoriteButton/FavoriteButton';
import { GetPathNameArgs, getPathname, getUrlWithDomain } from '@/i18n/navigation';
import { basketAtom, embroideryProductKeyAtom, showEmbroideryOnDetailPageAtom } from '@/store/atoms';
import { persistEmbroidery } from '@/utility/embroideryHelpers';
import { getOneSizeId } from '@/utility/getOneSizeId';
import { Locale } from '@/utility/locale';
import { tracker } from '@/utility/tracking/tracker';
import { CartFeatureDetail } from '@/utility/tracking/types';
import { getCYODesignerPageHrefArgs, getProductDetailPageHrefArgs, getSellableDetailHrefArgs } from '@/utility/urls';
import { ClientComposition, ClientSellableAndProductType } from '../utility/pdpGroups';
import { getAvailableAppearanceIds } from '../utility/trackingData';
import { AppearanceSelection } from './AppearanceSelection';
import { GroupedSellableSelector } from './GroupedSellableSelector';
export type MPActionsSectionProps = {
  publicApiConfig: PublicApiConfig;
  productType: ClientProductType;
  sellableGroupItems?: ClientSellableAndProductType[];
  selectedAppearanceId: string;
  sellable: Sellable;
  shopId: string;
  platform: Platform;
  locale: Locale;
  trackedPrice: number;
  priceGroup?: number;
  composition: ClientComposition;
  beforeAppearanceElement?: ReactNode;
  customPromoCode?: PromoCode;
  trackingPageType?: CartFeatureDetail;
  disableCustomization?: boolean;
  templateId?: string;
};
export const MPActionsSection = ({
  publicApiConfig,
  productType,
  sellableGroupItems,
  selectedAppearanceId: serverAppearanceId,
  sellable,
  shopId,
  platform,
  locale,
  trackedPrice,
  priceGroup,
  composition,
  beforeAppearanceElement,
  customPromoCode,
  trackingPageType = 'Product Detail Page',
  disableCustomization = false,
  templateId
}: MPActionsSectionProps) => {
  const t = useTranslations();
  const router = useRouter();
  const [openDialog, setOpenDialog] = useState<'size-dialog' | undefined>(undefined);
  const [pdpParams, setPdpParams] = useAtom(pdpParamsWithSyncAtom);
  const [sizeMissingError, setSizeMissingError] = useState<boolean>(false);
  const showEmbroidery = useAtomValue(showEmbroideryOnDetailPageAtom);
  const embroideryProductKey = useAtomValue(embroideryProductKeyAtom);
  const [embroideryProductId, setEmbroideryProductId] = useState<string>();
  const [isPersistingEmbroidery, setIsPersistingEmbroidery] = useState(false);
  const addItem = useAddItem(trackingPageType);
  const isDesktop = useIsDesktop();
  const isMultiCouponEnabled = useIsMultiCouponEnabled();
  const {
    mutate: applyCustomVoucher
  } = useAddVoucher({
    keepCartCoupons: isMultiCouponEnabled
  });
  const setOpenBasket = useSetAtom(basketAtom);
  const selectedSizeId = pdpParams?.sizeId;
  const selectedAppearanceId = pdpParams?.appearanceId ?? serverAppearanceId;
  const selectedAppearanceStockState = productType.stockStates.filter(stockState => stockState.appearance.id === selectedAppearanceId);
  const selectedSizesStockState = sellableGroupItems ?
  // When sellableGroup exists, consider all productTypes regarding stock
  sellableGroupItems.flatMap(sellableAndProductType => sellableAndProductType.productType.stockStates) : productType.stockStates.filter(stockState_0 => !selectedSizeId || stockState_0.size.id === selectedSizeId);
  const outOfStock = selectedAppearanceStockState.every(stockState_1 => !stockState_1.available);
  const isAddToCartDisabled = outOfStock || isPersistingEmbroidery;
  useEffect(() => {
    setEmbroideryProductId(undefined);
  }, [embroideryProductKey, selectedAppearanceId]);
  const updateSize = (sizeId: string) => {
    if (sizeId) {
      setPdpParams({
        sizeId
      });
      setSizeMissingError(false);
      tracker.trackFeature({
        featureUsed: 'choose-size',
        detail: sizeId
      });
      tracker.trackClientProduct({
        product: {
          sizeId,
          availableAppearances: getAvailableAppearanceIds(productType, sellable, sizeId)
        }
      });
    } else {
      setPdpParams({
        sizeId: undefined
      });
    }
  };
  const updateSellable = (sellableId: string) => {
    const sellable_0 = composition.sellables.find(s => s.sellableId === sellableId);

    // need to switch appearance too if new sellable does not support current appearance
    const appearanceId = sellable_0?.allowedAppearances.includes(Number(selectedAppearanceId)) ? selectedAppearanceId : undefined;

    // calculate correct pathname so no redirect is required
    const expectedHrefArgs = getSellableDetailHrefArgs({
      compositionId: composition.compositionId,
      sellableName: composition.name || '',
      productTypeName: sellableGroupItems?.find(sg => sg.sellable.sellableId === sellableId)?.productType.name || '',
      sellableId: sellableId,
      appearanceId: appearanceId || undefined
    });
    const fullPath = getPathname({
      href: expectedHrefArgs,
      locale
    });
    router.replace(fullPath, {
      scroll: false
    });
  };
  const getPathnameArgs = useCallback((appearanceId_0: string, sellableId_0?: string): GetPathNameArgs => {
    return {
      href: getSellableDetailHrefArgs({
        compositionId: composition.compositionId,
        sellableName: composition.name || '',
        productTypeName: sellableGroupItems?.find(sg_0 => sg_0.sellable.sellableId === sellableId_0)?.productType.name || productType.name,
        sellableId: sellableId_0 || (sellable.sellableId !== composition.defaultSellableId ? sellable.sellableId : undefined),
        appearanceId: appearanceId_0,
        sizeId: selectedSizeId ?? undefined
      }),
      locale
    };
  }, [composition, locale, sellable.sellableId, selectedSizeId, productType.name, sellableGroupItems]);
  const addToBasket = (sizeId_0?: string) => {
    if (isPersistingEmbroidery) {
      return;
    }
    const size = sizeId_0 || selectedSizeId || (sellableGroupItems ? productType.sizes[0]?.id : undefined);
    if (size === undefined) {
      setSizeMissingError(true);
      return;
    }
    const createEmbroideryProduct = async (size_0: string) => {
      if (embroideryProductKey) {
        setIsPersistingEmbroidery(true);
        try {
          const result = await persistEmbroidery(publicApiConfig, embroideryProductKey, selectedAppearanceId);
          if (result.productId) {
            setEmbroideryProductId(result.productId);
            createBasketItem(size_0, Number(result.productId));
          }
        } finally {
          setIsPersistingEmbroidery(false);
        }
      }
    };
    if (showEmbroidery && embroideryProductKey) {
      if (embroideryProductId) {
        createBasketItem(size, Number(embroideryProductId));
      } else {
        createEmbroideryProduct(size);
      }
    } else {
      createBasketItem(size);
    }
  };
  const createBasketItem = (size_1: string, embroideryProductId_0?: number) => {
    addItem.mutate({
      cartItems: [{
        productTypeId: Number(productType.id),
        productId: embroideryProductId_0,
        shopId: Number(shopId),
        appearanceId: selectedAppearanceId,
        sizeId: size_1,
        articleName: composition.name,
        articleId: composition.compositionId,
        sellableId: sellable.sellableId,
        quantity: 1,
        price: trackedPrice,
        channel: priceGroup ? 'licensingGroup' + priceGroup : undefined,
        editLink: getUrlWithDomain({
          href: getProductDetailPageHrefArgs({
            compositionId: composition.compositionId,
            sellableName: composition.name || '',
            productTypeName: sellableGroupItems?.find(sg_1 => sg_1.sellable.sellableId === sellable.sellableId)?.productType.name || productType.name,
            queryParams: {
              sellable: sellable.sellableId,
              appearance: Number(selectedAppearanceId),
              size: Number(size_1)
            }
          }),
          locale
        })
      }]
    }, {
      onSuccess: () => {
        // Active coupons get automatically applied in the cart. We only have to handle custom codes.
        if (customPromoCode) {
          applyCustomVoucher({
            coupons: [customPromoCode.code]
          });
        }
        setOpenDialog(undefined);
        setOpenBasket(true);
      }
    });
  };
  const isCustomizable = useMemo(() => {
    if (disableCustomization) return false;
    const isCustomizationAllowed = composition.designerMode === 'ENABLED';

    // Digital print types have an empty array for printColorIds
    // The Imgly designer doesn't support non-digital print types (e.g. flex, flock) yet
    const hasDigitalPrintTypesOnly = !sellable.printAreas.flatMap(printArea => printArea.printTypes).some(printType => printType.printColorIds.length > 0);
    return isCustomizationAllowed && hasDigitalPrintTypesOnly;
  }, [composition.designerMode, disableCustomization, sellable.printAreas]);
  const routeToDesigner = useCallback(() => {
    router.push(getPathname({
      href: getCYODesignerPageHrefArgs({
        sellable: sellable.sellableId,
        composition: composition.compositionId,
        appearance: selectedAppearanceId ? Number(selectedAppearanceId) : undefined,
        size: selectedSizeId ? Number(selectedSizeId) : undefined
      }),
      locale
    }));
  }, [router, sellable.sellableId, composition.compositionId, selectedAppearanceId, selectedSizeId, locale]);
  const routeToDesignerWithTemplate = () => {
    if (!templateId) return;
    tracker.trackFeature({
      featureUsed: 'template-personalize',
      detail: templateId
    });
    router.push(getPathname({
      href: getCYODesignerPageHrefArgs({
        template: templateId,
        productType: Number(productType.id),
        appearance: selectedAppearanceId ? Number(selectedAppearanceId) : undefined,
        size: selectedSizeId ? Number(selectedSizeId) : undefined,
        embroidery: showEmbroidery || undefined
      }),
      locale
    }));
  };
  const AppearanceElement = <AppearanceSelection productType={productType} sellable={sellable} sellableGroup={sellableGroupItems} selectedSizesStockState={selectedSizesStockState} getPathnameArgs={getPathnameArgs} />;
  return <>
      {sellableGroupItems ? <>
          <GroupedSellableSelector productType={productType} sellableGroup={sellableGroupItems} selectedSellable={sellable} onSellableSelection={(sellableAndProductType_0: ClientSellableAndProductType) => updateSellable(sellableAndProductType_0.sellable.sellableId)} platform={platform} error={sizeMissingError} />
          {AppearanceElement}
        </> : <>
          {beforeAppearanceElement}
          {AppearanceElement}
          <div className="hidden flex-col gap-2 md:flex">
            <SizeSelectorDesktop productType={productType} selectedAppearanceStockState={selectedAppearanceStockState} selectedSizeId={selectedSizeId} onSizeSelection={updateSize} platform={platform} error={sizeMissingError} />
          </div>
        </>}

      <div className="flex flex-row gap-3 md:flex-col">
        {isCustomizable && !templateId && <Button variant="gradient-border" className="w-full" size="l" onClick={routeToDesigner}>
            {t('mpDetailPage.customize')}
          </Button>}
        <div className="flex w-full items-center justify-between gap-3">
          {templateId ? <Button variant="gradient" className="w-full" size="l" disabled={outOfStock} onClick={routeToDesignerWithTemplate}>
              {t('mpDetailPage.personalizeAndAddToCart')}
            </Button> : <Button variant="primary" className="w-full" size="l" disabled={isAddToCartDisabled} onClick={() => {
          if (productType.sizes.length === 1 && !outOfStock && !sellableGroupItems) {
            const oneSizeId = productType.sizes[0]!.id;
            updateSize(oneSizeId);
            addToBasket(oneSizeId);
          } else {
            if (!isDesktop && !sellableGroupItems) {
              setOpenDialog('size-dialog');
            } else {
              addToBasket();
            }
          }
        }}>
              {t('mpDetailPage.addToCart')}
            </Button>}
          <FavoriteButton className="aspect-square p-2.5! max-md:hidden" testId="pdp-favorite-button" sellableId={sellable.sellableId} compositionId={composition.compositionId} productTypeId={productType.id} appearanceId={selectedAppearanceId} sizeId={selectedSizeId ?? getOneSizeId(productType)} shopId={Number(shopId)} label={composition.name} price={trackedPrice} variant="ghost" />
        </div>

        <Drawer dismissible open={openDialog === 'size-dialog'} title={t('mpDetailPage.selectSizeAndAddToCart')} closeLabel={t('general.close')} onOpenChange={open => open ? setOpenDialog('size-dialog') : setOpenDialog(undefined)} className="pb-12">
          <div className="flex flex-col gap-3">
            <SizeSelectorMobile productType={productType} selectedAppearanceStockState={selectedAppearanceStockState} selectedSizeId={selectedSizeId} onSizeSelection={sizeId_1 => {
            updateSize(sizeId_1);
            addToBasket(sizeId_1);
          }} platform={platform} />
          </div>
        </Drawer>
      </div>
    </>;
};